import { useApolloClient } from '@apollo/client'
import {
  Box,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import ArrowBack from '@material-ui/icons/ArrowBack'
import Delete from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import PersonAddIcon from '@material-ui/icons/PersonAdd'
import ThreeDRotation from '@material-ui/icons/ThreeDRotation'
import React, { useState } from 'react'
import { useHistory } from 'react-router'
import { useLocation } from 'react-router-dom'
import { CREATE_ORDER_MUTATION, UPDATE_ORDER } from '../../graphql/queries'
import { useGlobalState } from '../../providers/GlobalState'
import theme from '../../theme'
import { CancelButton, SuccessButton, WarningButton } from '../Buttons'
import BudgetAddCard from './BudgetAddCard'
import BudgetCard from './BudgetCard'
import BudgetDescount from './BudgetDescount'
import { OrderItemDialog } from './BudgetModal'
import { PacientDialog } from './PacientSelect'
import { useListStyles } from './styles'
import SuccessModal from './SuccessModal'
import { useBudgetForm } from './useBudgetForm'

const EstimateList: React.FC = () => {
  const {
    patient,
    setPatient,
    items,
    appendOrderItem,
    removeOrderItem,
    reset,
    discount,
    setDiscount,
    observation,
    setObservation,
    iols,
    addIol,
    removeIol,
    id,
    numberOs,
    updateIolSide
  } = useBudgetForm()
  const { iolLibraries } = useGlobalState()
  const [loading, setLoading] = useState(false)
  const [openPacientModal, setOpenPacientModal] = useState<boolean>(false)
  const [openBudgetModal, setOpenBudgetModal] = useState<boolean>(false)
  const classes = useListStyles()
  const router = useHistory()
  const { search } = useLocation()
  const params = React.useMemo(() => new URLSearchParams(search), [search])
  const history = useHistory()
  const client = useApolloClient()
  const [orderResponse, setOrderResponse] = useState<{
    message: string
    numberOs: string
  } | null>(null)

  const total = React.useMemo(() => {
    return (
      items.reduce((prev, item) => {
        return prev + item.price.float
      }, 0) +
      iols.reduce((prev, item) => {
        return prev + (item?.price?.float || 0)
      }, 0)
    )
  }, [items, iols])

  React.useEffect(() => {
    const slug = params.get('produto')
    const iolLibrary = iolLibraries?.find(iol => iol?.slug === slug)
    if (iolLibrary) {
      addIol({ ...iolLibrary, side: 'right' })
      addIol({ ...iolLibrary, side: 'left' })
      history.replace({
        search: ''
      })
    }
  }, [params])

  const save = (status: 'Sale' | 'Budge') => {
    setLoading(true)
    client
      .mutate({
        mutation: id ? UPDATE_ORDER : CREATE_ORDER_MUTATION,
        variables: {
          ...(id
            ? {
                filter: {
                  ids: [id]
                }
              }
            : {}),
          input: {
            iols: iols.map(iol => ({
              side: iol.side,
              typeId: iol?.iolType,
              typeName: iol?.__typename,
              iolId: iol?.id,
              iolName: iol?.name,
              price: iol?.price?.float,
              discount: 0
            })),
            status,
            customer: {
              ...patient,
              cpf: patient?.cpf?.replace(/[^\d]+/g, '')
            },
            payment: {
              amount: total,
              discount: discount,
              netAmount: total - discount
            },
            cart: items.map(item => ({
              price: item.price.float,
              discount: 0,
              name: item.name,
              code: item.code
            })),
            annotations: observation
          }
        }
      })
      .then(({ data }) => {
        if (id) {
          history.goBack()
        } else {
          setOrderResponse({
            message: data.createOrder.message,
            numberOs: data.createOrder.edges[0].numberOs
          })
        }

        reset()
        setLoading(false)
      })
  }

  const handleBudget = () => {
    save('Budge')
  }
  const handleSale = () => {
    save('Sale')
  }

  const handleClickOpenBudgetModal = () => {
    setOpenBudgetModal(true)
  }

  const handleCloseBudgetModal = () => {
    setOpenBudgetModal(false)
  }

  const handleClickOpenPacientModal = () => {
    setOpenPacientModal(true)
  }

  const handleClosePacientModal = () => {
    setOpenPacientModal(false)
  }

  const handleClickDeleteIOL = (index: number) => {
    console.info(index)
    removeIol(index)
  }

  const patientCardContent = React.useMemo(() => {
    if (!patient) {
      return (
        <span style={{ color: 'white' }}>
          Adicionar informações do paciente
        </span>
      )
    }
    return (
      <Box
        display="flex"
        width="100%"
        justifyContent="space-between"
        style={{ color: 'white' }}
        mr={2}
      >
        <div>
          <div style={{ fontSize: 12 }}>Paciente:</div>
          <div>{patient.name}</div>
        </div>
        <div>
          <div style={{ fontSize: 12 }}>CPF:</div>
          <div>{patient.cpf}</div>
        </div>
        <div>
          <div style={{ fontSize: 12 }}>Telefone:</div>
          <div>{patient.phone}</div>
        </div>
        <div>
          <div style={{ fontSize: 12 }}>Email:</div>
          <div>{patient.email}</div>
        </div>
      </Box>
    )
  }, [patient])

  return (
    <div className={classes.root}>
      <div className={classes.list}>
        <Box display="flex" justifyContent="center">
          {id && (
            <IconButton onClick={() => history.goBack()}>
              <ArrowBack />
            </IconButton>
          )}
          <h1 className={classes.title} style={{ margin: 0 }}>
            Orçamento Cirúrgico {id ? `#${numberOs}` : ''}
          </h1>
        </Box>
        <h2 className={classes.description}>
          Produtos no pacote do orçamento cirúrgico
        </h2>
        <BudgetAddCard
          style={{
            background: !patient
              ? theme.palette.error.main
              : theme.palette.success.main
          }}
          fontStyle={{
            color: 'white'
          }}
          iconStyle={{
            color: 'white'
          }}
          onClick={handleClickOpenPacientModal}
          title={patientCardContent}
          icon={!patient ? PersonAddIcon : EditIcon}
          toolTip="Criar paciente"
        />
        {!iols.length && (
          <BudgetAddCard
            style={{
              background: theme.palette.error.main
            }}
            fontStyle={{
              color: 'white'
            }}
            iconStyle={{
              color: 'white'
            }}
            onClick={() =>
              history.push(`/nav/biblioteca/${iolLibraries?.[0]?.slug}`)
            }
            title={
              <span style={{ color: 'white' }}>
                Você ainda não selecionou uma lente
              </span>
            }
            icon={ThreeDRotation}
            toolTip="Selecionar lente"
          />
        )}
        <BudgetAddCard
          title="Adicionar novo produto"
          onClick={handleClickOpenBudgetModal}
          toolTip="Criar ou adicionar novo produto"
        />
        <div className={classes.overflow}>
          {iols.map((iol, i) => (
            <BudgetCard
              key={`${iol.id}-${i}`}
              onChangeSide={newSide => updateIolSide(i, newSide)}
              procedureItem={{
                code: '',
                name: iol.name,
                price: {
                  float: iol.price.float || 0
                },
                side: iol.side
              }}
              icon={
                <Tooltip title="Remover do orçamento">
                  <IconButton onClick={() => handleClickDeleteIOL(i)}>
                    <Delete color="error" />
                  </IconButton>
                </Tooltip>
              }
            />
          ))}
          {items.map((item, index) => (
            <BudgetCard
              procedureItem={item}
              key={index}
              icon={
                <Tooltip title="Remover do orçamento">
                  <IconButton onClick={() => removeOrderItem(index)}>
                    <Delete color="error" />
                  </IconButton>
                </Tooltip>
              }
            />
          ))}
          <BudgetDescount
            descount={discount}
            procedure={{
              name: 'Adicionar Desconto',
              totalPrice: { format: `${discount}` },
              id: '9',
              items: []
            }}
            handleChange={number => setDiscount(number)}
          />
        </div>
        <div>
          <Grid container justify="space-between">
            <Grid xs={9}>
              <TextField
                type="text"
                name="slug"
                rows={5}
                multiline
                value={observation}
                onChange={e => setObservation(e.target.value)}
                variant="outlined"
                label="Observações"
                helperText="Esta campo não vai aparecer para os usuários"
                fullWidth
              />
            </Grid>
            <Grid xs={3} style={{ textAlign: 'right', paddingLeft: 15 }}>
              <Typography variant="body1" style={{ padding: 5 }}>
                Total:{' '}
                <strong>
                  {total.toLocaleString('pt-br', {
                    style: 'currency',
                    currency: 'BRL'
                  })}
                </strong>
              </Typography>
              <Typography variant="body1" style={{ padding: 5 }}>
                Desconto:{' '}
                <strong style={{ color: 'green' }}>
                  {discount.toLocaleString('pt-br', {
                    style: 'currency',
                    currency: 'BRL'
                  })}
                </strong>
              </Typography>
              <Typography
                variant="body1"
                style={{ background: '#e9e9e9', padding: 5 }}
              >
                Subtotal:{' '}
                <strong>
                  {(total - discount).toLocaleString('pt-br', {
                    style: 'currency',
                    currency: 'BRL'
                  })}
                </strong>
              </Typography>
            </Grid>
          </Grid>
        </div>
        <br />
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-evenly'
          }}
        >
          <CancelButton
            variant="contained"
            onClick={() => {
              reset()
              router.goBack()
            }}
          >
            Cancelar
          </CancelButton>
          <WarningButton
            disabled={
              !patient || !iols.length || loading || total - discount < 0
            }
            variant="contained"
            color="primary"
            onClick={handleBudget}
          >
            {loading ? 'Salvando...' : 'Salvar como orçamento'}
          </WarningButton>
          <SuccessButton
            disabled={
              !patient || !iols.length || loading || total - discount < 0
            }
            variant="contained"
            color="primary"
            onClick={handleSale}
          >
            {loading ? 'Salvando...' : 'Finalizar venda'}
          </SuccessButton>
        </div>
      </div>
      {openPacientModal && (
        <PacientDialog
          handleClose={handleClosePacientModal}
          patient={patient}
          open={openPacientModal}
          handleChange={p => setPatient(p)}
        />
      )}
      {openBudgetModal && (
        <OrderItemDialog
          handleChange={appendOrderItem}
          handleClose={handleCloseBudgetModal}
        />
      )}
      {orderResponse && (
        <SuccessModal
          handleClose={() => setOrderResponse(null)}
          orderResponse={orderResponse}
        />
      )}
    </div>
  )
}

export default EstimateList
